// Tier-1 display directives. Node-type per directive per the post-
// normalizeAgentMarkdown matrix:
//   callout / steps / step / card-group / stat-group → containerDirective
//   stat / card → textDirective (corpus form) | leafDirective (fallback)
//
// See apps/web/src/components/canvas/markdown-renderer.tsx for the
// production rendering of these directives. Phase 1 mirrors the wire
// format only — we render simple, read-only stubs that exercise the
// descriptor pipeline; Phase 4 brings full styled renders.
//
// Phase 1 inline styles below give each directive a clearly-bounded
// visual treatment so the surface visually distinguishes a directive
// from surrounding prose. Tailwind / design-system tokens land in
// Phase 4 when live mounts replace these stubs.

import type { CSSProperties } from 'react';
import { NestedLexicalEditor, type DirectiveDescriptor } from '@mdxeditor/editor';

// Helpers to render the body of a containerDirective via MDXEditor's
// NestedLexicalEditor primitive. We strip the directive-label child
// (the bracket payload like `info` in `:::callout[info]`) so it doesn't
// appear as prose; the variant is already shown in the chip header.
const isDirectiveLabel = (child: any) => child?.data?.directiveLabel === true;

function bodyContent(node: any): any[] {
  return (node?.children ?? []).filter((c: any) => !isDirectiveLabel(c));
}

function reattachLabel(node: any, body: any[]): any {
  const label = (node?.children ?? []).find((c: any) => isDirectiveLabel(c));
  return { ...node, children: label ? [label, ...body] : body };
}

const isContainerDirective = (node: any, name: string) =>
  node?.type === 'containerDirective' && node?.name === name;

const isInlineOrLeafDirective = (node: any, name: string) =>
  (node?.type === 'textDirective' || node?.type === 'leafDirective') &&
  node?.name === name;

const CALLOUT_THEME: Record<string, { border: string; bg: string; bar: string }> = {
  info: { border: '#bfdbfe', bg: '#eff6ff', bar: '#3b82f6' },
  warning: { border: '#fde68a', bg: '#fffbeb', bar: '#f59e0b' },
  tip: { border: '#bbf7d0', bg: '#f0fdf4', bar: '#10b981' },
  danger: { border: '#fecaca', bg: '#fef2f2', bar: '#dc2626' },
  success: { border: '#bbf7d0', bg: '#f0fdf4', bar: '#10b981' },
};

const calloutStyle = (variant: string): CSSProperties => {
  const theme = CALLOUT_THEME[variant] ?? CALLOUT_THEME.info;
  return {
    display: 'block',
    border: `1px solid ${theme.border}`,
    borderLeft: `3px solid ${theme.bar}`,
    borderRadius: 4,
    padding: '8px 12px',
    margin: '12px 0',
    background: theme.bg,
    fontFamily: 'system-ui, sans-serif',
    fontSize: 13,
  };
};

const stepsStyle: CSSProperties = {
  display: 'block',
  margin: '12px 0',
  padding: '0 0 0 16px',
  borderLeft: '2px solid #e5e7eb',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
};

const stepStyle = (status: string): CSSProperties => {
  const bar =
    status === 'done' ? '#10b981' :
    status === 'active' ? '#3b82f6' :
    status === 'pending' ? '#9ca3af' :
    '#cbd5e1';
  return {
    display: 'block',
    padding: '6px 10px',
    margin: '6px 0',
    borderLeft: `3px solid ${bar}`,
    background: '#f9fafb',
    borderRadius: 4,
    fontFamily: 'system-ui, sans-serif',
    fontSize: 13,
  };
};

const cardStyle: CSSProperties = {
  display: 'inline-block',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '8px 12px',
  margin: '6px 8px 6px 0',
  background: '#ffffff',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
  minWidth: 140,
};

const cardGroupStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  margin: '12px 0',
};

const statStyle: CSSProperties = {
  display: 'inline-block',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  padding: '6px 10px',
  margin: '4px 6px 4px 0',
  background: '#f9fafb',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
};

const statGroupStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  margin: '12px 0',
};

const faqStyle: CSSProperties = {
  display: 'block',
  margin: '12px 0',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
};

const qStyle: CSSProperties = {
  display: 'block',
  margin: '6px 0',
  padding: '8px 10px',
  border: '1px solid #e5e7eb',
  borderLeft: '3px solid #6366f1',
  borderRadius: 4,
  background: '#f9fafb',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 13,
};

const calloutDescriptor: DirectiveDescriptor<any> = {
  name: 'callout',
  testNode: (node) => isContainerDirective(node, 'callout'),
  attributes: ['title'],
  hasChildren: true,
  Editor: ({ mdastNode }: any) => {
    const variant =
      (mdastNode?.children?.[0]?.children?.[0]?.value as string) ?? 'info';
    const title = (mdastNode?.attributes?.title as string) ?? '';
    return (
      <div
        data-testid={`callout-${variant}`}
        className={`rmint-callout rmint-callout-${variant}`}
        style={calloutStyle(variant)}
      >
        <span style={{ color: '#6b7280', fontSize: 11, display: 'block', marginBottom: 4 }}>
          callout · {variant}
        </span>
        {title && (
          <strong style={{ display: 'block', marginBottom: 4 }}>{title}</strong>
        )}
        <NestedLexicalEditor
          block
          getContent={bodyContent}
          getUpdatedMdastNode={reattachLabel}
        />
      </div>
    );
  },
};

const stepsDescriptor: DirectiveDescriptor<any> = {
  name: 'steps',
  testNode: (node) => isContainerDirective(node, 'steps'),
  attributes: [],
  hasChildren: true,
  Editor: () => (
    <div data-testid="steps" className="rmint-steps" style={stepsStyle}>
      <span style={{ color: '#6b7280', fontSize: 11, display: 'block', marginBottom: 4 }}>
        steps
      </span>
      <NestedLexicalEditor
        block
        getContent={bodyContent}
        getUpdatedMdastNode={reattachLabel}
      />
    </div>
  ),
};

const stepDescriptor: DirectiveDescriptor<any> = {
  name: 'step',
  testNode: (node) => isContainerDirective(node, 'step'),
  attributes: ['title', 'status'],
  hasChildren: true,
  Editor: ({ mdastNode }: any) => {
    const title = (mdastNode?.attributes?.title as string) ?? '';
    const status = (mdastNode?.attributes?.status as string) ?? '';
    return (
      <div
        data-testid={`step-${status || 'none'}`}
        className="rmint-step"
        style={stepStyle(status)}
      >
        <div style={{ marginBottom: 4 }}>
          <strong>{title}</strong>
          {status && (
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 11 }}>
              · {status}
            </span>
          )}
        </div>
        <NestedLexicalEditor
          block
          getContent={bodyContent}
          getUpdatedMdastNode={reattachLabel}
        />
      </div>
    );
  },
};

const cardDescriptor: DirectiveDescriptor<any> = {
  name: 'card',
  testNode: (node) => isInlineOrLeafDirective(node, 'card'),
  attributes: ['title', 'icon', 'href'],
  hasChildren: false,
  Editor: ({ mdastNode }: any) => {
    const title = (mdastNode?.attributes?.title as string) ?? '';
    return (
      <div data-testid="card" className="rmint-card" style={cardStyle}>
        <strong>{title}</strong>
      </div>
    );
  },
};

const cardGroupDescriptor: DirectiveDescriptor<any> = {
  name: 'card-group',
  testNode: (node) => isContainerDirective(node, 'card-group'),
  attributes: ['cols'],
  hasChildren: true,
  Editor: () => (
    <div data-testid="card-group" className="rmint-card-group" style={cardGroupStyle}>
      <span style={{ color: '#6b7280', fontSize: 11, width: '100%' }}>card-group</span>
      <NestedLexicalEditor
        block
        getContent={bodyContent}
        getUpdatedMdastNode={reattachLabel}
      />
    </div>
  ),
};

const statDescriptor: DirectiveDescriptor<any> = {
  name: 'stat',
  testNode: (node) => isInlineOrLeafDirective(node, 'stat'),
  attributes: ['label', 'value', 'change'],
  hasChildren: false,
  Editor: ({ mdastNode }: any) => {
    const a = mdastNode?.attributes ?? {};
    return (
      <div data-testid="stat" className="rmint-stat" style={statStyle}>
        <span style={{ color: '#6b7280' }}>{a.label as string}</span>:{' '}
        <strong>{a.value as string}</strong>
        {a.change && <span style={{ color: '#10b981', marginLeft: 6 }}>{a.change as string}</span>}
      </div>
    );
  },
};

const statGroupDescriptor: DirectiveDescriptor<any> = {
  name: 'stat-group',
  testNode: (node) => isContainerDirective(node, 'stat-group'),
  attributes: [],
  hasChildren: true,
  Editor: () => (
    <div data-testid="stat-group" className="rmint-stat-group" style={statGroupStyle}>
      <span style={{ color: '#6b7280', fontSize: 11, width: '100%' }}>stat-group</span>
      <NestedLexicalEditor
        block
        getContent={bodyContent}
        getUpdatedMdastNode={reattachLabel}
      />
    </div>
  ),
};

const faqDescriptor: DirectiveDescriptor<any> = {
  name: 'faq',
  testNode: (node) => isContainerDirective(node, 'faq'),
  attributes: [],
  hasChildren: true,
  Editor: () => (
    <div data-testid="faq" className="rmint-faq" style={faqStyle}>
      <span style={{ color: '#6b7280', fontSize: 11, display: 'block', marginBottom: 4 }}>
        faq
      </span>
      <NestedLexicalEditor
        block
        getContent={bodyContent}
        getUpdatedMdastNode={reattachLabel}
      />
    </div>
  ),
};

const qDescriptor: DirectiveDescriptor<any> = {
  name: 'q',
  testNode: (node) => isContainerDirective(node, 'q'),
  attributes: [],
  hasChildren: true,
  Editor: ({ mdastNode }: any) => {
    const question =
      (mdastNode?.children?.[0]?.children?.[0]?.value as string) ?? '';
    return (
      <div data-testid="faq-q" className="rmint-faq-q" style={qStyle}>
        <strong style={{ display: 'block', marginBottom: 4 }}>
          {question || '(no question)'}
        </strong>
        <NestedLexicalEditor
          block
          getContent={bodyContent}
          getUpdatedMdastNode={reattachLabel}
        />
      </div>
    );
  },
};

export const displayDirectiveDescriptors: DirectiveDescriptor<any>[] = [
  calloutDescriptor,
  stepsDescriptor,
  stepDescriptor,
  cardDescriptor,
  cardGroupDescriptor,
  statDescriptor,
  statGroupDescriptor,
  faqDescriptor,
  qDescriptor,
];
